import { useState, useMemo } from 'react';
import { Settings, Download, Upload, Eye, Plus, X, Trash2, Search } from 'lucide-react';
import { useLocalStorage } from '../hooks.js';
import { APPT_DATA } from './DashboardView.jsx';

export function CompanyAppointmentsView({ appointments, onAppointmentsChange, onToggleCell, onBulkReplace, customApptData: customApptDataProp, onCustomApptDataChange, lang, userId, readOnly, currentUser }) {
  const ALL_US_STATES = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","DC","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"];

  // customApptData is now hoisted in deskte-full and synced to the server
  // via PUT /carrier-appointments/custom. Fall back to the legacy
  // localStorage path in demo mode (no auth) so nothing breaks.
  const [localCustomApptData, setLocalCustomApptData] = useLocalStorage(
    "dkt6_customApptData",
    { categories: {}, carriers: {} },
  );
  const customApptData = customApptDataProp ?? localCustomApptData;
  const setCustomApptData = onCustomApptDataChange || setLocalCustomApptData;
  const [tab, setTab] = useState("health");
  const [searchState, setSearchState] = useState("");
  const [showAddCarrier, setShowAddCarrier] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [newCarrier, setNewCarrier] = useState("");
  const [newCatKey, setNewCatKey] = useState("");
  const [newCatLabel, setNewCatLabel] = useState("");
  const [exportMsg, setExportMsg] = useState("");
  // ANCHO DE LA COLUMNA "ESTADO" (Lissette 2026-10-13: "ese espacio está muy
  // largo, ¿no podemos acortarlo arrastrando?"). Se arrastra el borde derecho
  // de la cabecera; doble clic vuelve al ancho de siempre. Se recuerda en este
  // navegador: es una comodidad de quien mira, no un dato de la agencia.
  const ANCHO_ESTADO = 170;
  const [anchoEstado, setAnchoEstado] = useLocalStorage("dkt_apptAnchoEstado", ANCHO_ESTADO);
  const ancho = Math.max(90, Math.min(600, Number(anchoEstado) || ANCHO_ESTADO));
  const empezarArrastre = (e) => {
    e.preventDefault();
    const x0 = e.clientX;
    const w0 = ancho;
    const mover = (ev) => setAnchoEstado(Math.max(90, Math.min(600, Math.round(w0 + ev.clientX - x0))));
    const soltar = () => {
      window.removeEventListener("pointermove", mover);
      window.removeEventListener("pointerup", soltar);
    };
    window.addEventListener("pointermove", mover);
    window.addEventListener("pointerup", soltar);
  };
  const es = lang === "es";
  const prefix = userId ? `${userId}::` : "";

  const mergedData = useMemo(() => {
    const merged = {};
    Object.entries(APPT_DATA).forEach(([key, val]) => {
      const extraCarriers = (customApptData.carriers && customApptData.carriers[key]) || [];
      // Built-in carriers the user chose to remove/hide (Lissette 2026-07-14).
      const hidden = (customApptData.hiddenCarriers && customApptData.hiddenCarriers[key]) || [];
      merged[key] = {
        label: val.label,
        carriers: [...val.carriers.filter(c => !hidden.includes(c)), ...extraCarriers.filter(c => !val.carriers.includes(c))],
        states: val.states,
        isCustom: false
      };
    });
    if (customApptData.categories) {
      Object.entries(customApptData.categories).forEach(([key, cat]) => {
        if (!merged[key]) {
          // Carriers added via addCarrier() land in customApptData.carriers[key]
          // (same top-level map as built-ins). Older data may sit in cat.carriers.
          // Merge BOTH so custom categories actually show the companies the user
          // adds — otherwise "Agregar Compañía" saves to a place the table never
          // reads and nothing appears (Lissette 2026-07-24).
          const legacy = cat.carriers || [];
          const extra = (customApptData.carriers && customApptData.carriers[key]) || [];
          merged[key] = {
            label: cat.label,
            carriers: [...legacy, ...extra.filter(c => !legacy.includes(c))],
            states: ALL_US_STATES.map(s => ({ s, c: {} })),
            isCustom: true
          };
        }
      });
    }
    return merged;
  }, [customApptData]);

  const validTab = mergedData[tab] ? tab : "health";
  const data = mergedData[validTab];
  const filteredStates = data.states.filter(st => !searchState || st.s.toLowerCase().includes(searchState.toLowerCase()));
  const toggle = (state, carrier) => {
    if (readOnly) return;
    const key = `${prefix}${validTab}__${state}__${carrier}`;
    // Optimistic local flip first so the cell responds instantly; the
    // server-backed callback (if present) reconciles in the background.
    onAppointmentsChange(prev => ({ ...prev, [key]: !prev[key] }));
    if (onToggleCell && !prefix) onToggleCell(validTab, state, carrier);
  };
  const isOn = (state, carrier) => !!appointments[`${prefix}${validTab}__${state}__${carrier}`];
  const countOn = data.states.reduce((sum, st) => sum + data.carriers.filter(c => isOn(st.s, c)).length, 0);
  const totalCells = data.states.length * data.carriers.length;

  const addCarrier = () => {
    const name = newCarrier.trim();
    if (!name || data.carriers.includes(name)) return;
    const isHiddenBuiltIn = APPT_DATA[validTab] && APPT_DATA[validTab].carriers.includes(name);
    setCustomApptData(prev => {
      // Re-adding a previously hidden built-in just un-hides it (no duplicate).
      if (isHiddenBuiltIn) {
        const hiddenCarriers = { ...(prev.hiddenCarriers || {}) };
        hiddenCarriers[validTab] = (hiddenCarriers[validTab] || []).filter(c => c !== name);
        return { ...prev, hiddenCarriers };
      }
      const carriers = { ...(prev.carriers || {}) };
      carriers[validTab] = [...(carriers[validTab] || []), name];
      return { ...prev, carriers };
    });
    setNewCarrier("");
    setShowAddCarrier(false);
  };
  // Remove a carrier: built-ins are HIDDEN (reversible via restore); custom
  // carriers are deleted from the custom list (Lissette 2026-07-14 — poder
  // quitar compañías aunque estén predeterminadas).
  const removeCarrier = (carrier) => {
    const isBuiltIn = APPT_DATA[validTab] && APPT_DATA[validTab].carriers.includes(carrier);
    setCustomApptData(prev => {
      if (isBuiltIn) {
        const hiddenCarriers = { ...(prev.hiddenCarriers || {}) };
        hiddenCarriers[validTab] = [...new Set([...(hiddenCarriers[validTab] || []), carrier])];
        return { ...prev, hiddenCarriers };
      }
      const carriers = { ...(prev.carriers || {}) };
      carriers[validTab] = (carriers[validTab] || []).filter(c => c !== carrier);
      return { ...prev, carriers };
    });
  };
  const restoreCarrier = (carrier) => {
    setCustomApptData(prev => {
      const hiddenCarriers = { ...(prev.hiddenCarriers || {}) };
      hiddenCarriers[validTab] = (hiddenCarriers[validTab] || []).filter(c => c !== carrier);
      return { ...prev, hiddenCarriers };
    });
  };
  const addCategory = () => {
    const key = newCatKey.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    const label = newCatLabel.trim();
    if (!key || !label || mergedData[key]) return;
    setCustomApptData(prev => {
      const categories = { ...(prev.categories || {}) };
      categories[key] = { label, carriers: [] };
      return { ...prev, categories };
    });
    setNewCatKey("");
    setNewCatLabel("");
    setShowAddCategory(false);
    setTab(key);
  };
  const removeCategory = (key) => {
    if (APPT_DATA[key]) return;
    setCustomApptData(prev => {
      const categories = { ...(prev.categories || {}) };
      const carriers = { ...(prev.carriers || {}) };
      delete categories[key];
      delete carriers[key];
      return { ...prev, categories, carriers };
    });
    if (validTab === key) setTab("health");
  };

  const exportConfig = () => {
    const blob = new Blob([JSON.stringify({ customApptData, appointments }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const _d = new Date();
    const _today = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,"0")}-${String(_d.getDate()).padStart(2,"0")}`;
    a.download = `deskte-appointments-${_today}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMsg(es ? "Exportado!" : "Exported!");
    setTimeout(() => setExportMsg(""), 2000);
  };
  const importConfig = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e) => {
      try {
        const text = await e.target.files[0].text();
        const parsed = JSON.parse(text);
        if (parsed.customApptData) setCustomApptData(parsed.customApptData);
        if (parsed.appointments) {
          onAppointmentsChange(parsed.appointments);
          if (onBulkReplace) onBulkReplace(parsed.appointments, parsed.customApptData);
        }
        setExportMsg(es ? "Importado!" : "Imported!");
        setTimeout(() => setExportMsg(""), 2000);
      } catch { setExportMsg(es ? "Error al importar" : "Import error"); setTimeout(() => setExportMsg(""), 2000); }
    };
    input.click();
  };

  const customCarriersForTab = (customApptData.carriers && customApptData.carriers[validTab]) || [];
  const hiddenForTab = (customApptData.hiddenCarriers && customApptData.hiddenCarriers[validTab]) || [];
  const isCustomCategory = !APPT_DATA[validTab];

  const btnSm = { padding:".35rem .65rem",borderRadius:7,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontSize:".72rem",fontWeight:600,cursor:"pointer",fontFamily:"inherit",transition:"all .15s",display:"inline-flex",alignItems:"center",gap:4 };
  const btnAccent = { ...btnSm, border:"1px solid rgba(0,212,255,.4)",color:"var(--c-cyan,#00d4ff)",background:"rgba(0,212,255,.08)" };
  const inputSm = { padding:".4rem .6rem",borderRadius:7,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-white,#eef6ff)",fontSize:".78rem",fontFamily:"inherit",outline:"none",width:"100%" };

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"1.25rem",flexWrap:"wrap",gap:".75rem"}}>
        <div>
          <h2 style={{fontSize:"1.1rem",fontWeight:800,color:"var(--c-white,#eef6ff)",margin:0}}>
            {es ? "Citas con Compañías" : "Company Appointments"}
          </h2>
          <div style={{fontSize:".75rem",color:"var(--c-text3,#45688a)",marginTop:4}}>
            {es ? "Gestiona tus citas por tipo de seguro y estado" : "Manage your carrier appointments by insurance type and state"}
            {" · "}<span style={{color:"var(--c-green,#00ff88)",fontWeight:700}}>{countOn}</span>/{totalCells}
          </div>
        </div>
        <div style={{display:"flex",gap:".4rem",alignItems:"center",flexWrap:"wrap"}}>
          {!readOnly && (
            <>
              <button onClick={()=>setShowManage(!showManage)} style={btnSm} title={es?"Gestionar":"Manage"}>
                <Settings size={13}/> {es?"Gestionar":"Manage"}
              </button>
              <button onClick={exportConfig} style={btnSm} title={es?"Exportar config":"Export config"}>
                <Download size={13}/> {es?"Exportar":"Export"}
              </button>
              <button onClick={importConfig} style={btnSm} title={es?"Importar config":"Import config"}>
                <Upload size={13}/> {es?"Importar":"Import"}
              </button>
            </>
          )}
          {readOnly && (
            <span style={{display:"flex",alignItems:"center",gap:5,background:"rgba(0,212,255,.1)",border:"1px solid rgba(0,212,255,.3)",color:"var(--c-cyan,#00d4ff)",fontSize:".7rem",fontWeight:700,padding:".4rem .8rem",borderRadius:9}}>
              <Eye size={12}/> {es ? "Solo lectura" : "View only"}
            </span>
          )}
          {exportMsg && <span style={{fontSize:".72rem",color:"var(--c-green,#00ff88)",fontWeight:600}}>{exportMsg}</span>}
        </div>
      </div>

      {/* Tabs: categories + add custom */}
      <div style={{display:"flex",gap:".4rem",flexWrap:"wrap",marginBottom:"1rem",alignItems:"center"}}>
        {Object.entries(mergedData).map(([key, val]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{padding:".45rem .9rem",borderRadius:8,border:`1px solid ${validTab===key?"rgba(0,212,255,.5)":"var(--c-border,#1a3550)"}`,
              background:validTab===key?"rgba(0,212,255,.15)":"var(--c-surface2,#112538)",
              color:validTab===key?"var(--c-cyan,#00d4ff)":"var(--c-text2,#7aa3c0)",
              fontSize:".78rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all .15s",display:"inline-flex",alignItems:"center",gap:4}}>
            {val.label}
            {val.isCustom && <span style={{fontSize:".55rem",background:"rgba(0,212,255,.2)",padding:"1px 5px",borderRadius:4,color:"var(--c-cyan,#00d4ff)"}}>custom</span>}
          </button>
        ))}
        {!readOnly && (
          <button onClick={()=>setShowAddCategory(!showAddCategory)} style={{...btnAccent,borderStyle:"dashed"}} title={es?"Agregar categoría":"Add category"}>
            <Plus size={13}/> {es?"Categoría":"Category"}
          </button>
        )}
      </div>

      {/* Add Category inline form */}
      {showAddCategory && (
        <div style={{marginBottom:"1rem",padding:".75rem",borderRadius:10,border:"1px solid rgba(0,212,255,.3)",background:"rgba(0,212,255,.05)",display:"flex",gap:".5rem",alignItems:"flex-end",flexWrap:"wrap"}}>
          <div style={{flex:"1 1 140px"}}>
            <label style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",display:"block",marginBottom:3}}>{es?"ID (sin espacios)":"ID (no spaces)"}</label>
            <input value={newCatKey} onChange={e=>setNewCatKey(e.target.value)} placeholder="e.g. commercial" style={inputSm}/>
          </div>
          <div style={{flex:"1 1 180px"}}>
            <label style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",display:"block",marginBottom:3}}>{es?"Nombre visible":"Display name"}</label>
            <input value={newCatLabel} onChange={e=>setNewCatLabel(e.target.value)} placeholder="e.g. Commercial Lines" style={inputSm}/>
          </div>
          <button onClick={addCategory} style={{...btnAccent,padding:".4rem .8rem"}} disabled={!newCatKey.trim()||!newCatLabel.trim()}>
            <Plus size={13}/> {es?"Crear":"Create"}
          </button>
          <button onClick={()=>setShowAddCategory(false)} style={btnSm}><X size={13}/></button>
        </div>
      )}

      {/* Manage panel */}
      {showManage && (
        <div style={{marginBottom:"1rem",padding:".75rem 1rem",borderRadius:10,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)"}}>
          <div style={{fontSize:".8rem",fontWeight:700,color:"var(--c-white,#eef6ff)",marginBottom:".5rem"}}>{es?"Gestionar: ":"Manage: "}{data.label}</div>

          {/* Custom carriers for this tab */}
          {customCarriersForTab.length > 0 && (
            <div style={{marginBottom:".5rem"}}>
              <div style={{fontSize:".68rem",color:"var(--c-text3,#45688a)",marginBottom:4}}>{es?"Compañías personalizadas":"Custom carriers"}:</div>
              <div style={{display:"flex",gap:".3rem",flexWrap:"wrap"}}>
                {customCarriersForTab.map(c => (
                  <span key={c} style={{display:"inline-flex",alignItems:"center",gap:3,padding:".25rem .55rem",borderRadius:6,background:"rgba(0,212,255,.1)",border:"1px solid rgba(0,212,255,.3)",fontSize:".7rem",color:"var(--c-cyan,#00d4ff)"}}>
                    {c}
                    <button onClick={()=>removeCarrier(c)} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",padding:0,display:"flex"}}><X size={11}/></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Hidden built-in carriers — click to restore */}
          {hiddenForTab.length > 0 && (
            <div style={{marginBottom:".5rem"}}>
              <div style={{fontSize:".68rem",color:"var(--c-text3,#45688a)",marginBottom:4}}>{es?"Compañías ocultas (clic para restaurar)":"Hidden carriers (click to restore)"}:</div>
              <div style={{display:"flex",gap:".3rem",flexWrap:"wrap"}}>
                {hiddenForTab.map(c => (
                  <button key={c} onClick={()=>restoreCarrier(c)} title={es?"Restaurar":"Restore"} style={{display:"inline-flex",alignItems:"center",gap:4,padding:".25rem .55rem",borderRadius:6,background:"rgba(100,116,139,.12)",border:"1px solid var(--c-border,#1a3550)",fontSize:".7rem",color:"var(--c-text3,#45688a)",cursor:"pointer",fontFamily:"inherit"}}>
                    {c} <span style={{color:"#34d399",fontWeight:700}}>↩</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Remove custom category */}
          {isCustomCategory && (
            <button onClick={()=>{removeCategory(validTab);setShowManage(false);}} style={{...btnSm,color:"#ef4444",border:"1px solid rgba(239,68,68,.4)",marginTop:".25rem"}}>
              <Trash2 size={12}/> {es?"Eliminar categoría":"Delete category"}
            </button>
          )}

          {/* List all custom categories for quick reference */}
          {Object.entries(customApptData.categories || {}).length > 0 && (
            <div style={{marginTop:".5rem",borderTop:"1px solid var(--c-border,#1a3550)",paddingTop:".5rem"}}>
              <div style={{fontSize:".68rem",color:"var(--c-text3,#45688a)",marginBottom:4}}>{es?"Todas las categorías custom":"All custom categories"}:</div>
              <div style={{display:"flex",gap:".3rem",flexWrap:"wrap"}}>
                {Object.entries(customApptData.categories).map(([k, v]) => (
                  <span key={k} style={{display:"inline-flex",alignItems:"center",gap:3,padding:".25rem .55rem",borderRadius:6,background:"rgba(139,92,246,.1)",border:"1px solid rgba(139,92,246,.3)",fontSize:".7rem",color:"#a78bfa"}}>
                    {v.label}
                    <button onClick={()=>removeCategory(k)} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",padding:0,display:"flex"}}><X size={11}/></button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add carrier inline */}
      <div style={{display:"flex",gap:".4rem",alignItems:"center",marginBottom:".875rem",flexWrap:"wrap"}}>
        <div style={{position:"relative",flex:"1 1 200px",maxWidth:300}}>
          <Search size={13} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--c-text3,#45688a)",pointerEvents:"none"}}/>
          <input value={searchState} onChange={e=>setSearchState(e.target.value)}
            placeholder={es?"Buscar estado...":"Search state..."}
            className="dkt-search" style={{paddingLeft:32,fontSize:".78rem"}}/>
        </div>
        {!readOnly && (showAddCarrier ? (
          <div style={{display:"flex",gap:".3rem",alignItems:"center"}}>
            <input value={newCarrier} onChange={e=>setNewCarrier(e.target.value)}
              placeholder={es?"Nombre de compañía...":"Carrier name..."}
              style={{...inputSm,width:180}}
              onKeyDown={e=>e.key==="Enter"&&addCarrier()}
              autoFocus/>
            <button onClick={addCarrier} style={btnAccent} disabled={!newCarrier.trim()}><Plus size={13}/></button>
            <button onClick={()=>{setShowAddCarrier(false);setNewCarrier("");}} style={btnSm}><X size={13}/></button>
          </div>
        ) : (
          <button onClick={()=>setShowAddCarrier(true)} style={{...btnAccent,borderStyle:"dashed"}}>
            <Plus size={13}/> {es?"Agregar Compañía":"Add Carrier"}
          </button>
        ))}
      </div>

      {/* maxHeight + overflow:auto → el scroll vertical vive DENTRO del contenedor,
          requisito para que el thead sticky (top:0) se quede pegado al bajar (Lissette 2026-07-16).
          borderCollapse:"separate" porque con "collapse" los bordes de celdas sticky se van con el scroll. */}
      <div style={{overflow:"auto",maxHeight:"calc(100vh - 210px)",borderRadius:12,border:"1px solid var(--c-border,#1a3550)"}}>
        <table style={{width:"auto",borderCollapse:"separate",borderSpacing:0,fontSize:".7rem",fontFamily:"inherit"}}>
          <thead>
            <tr style={{background:"var(--c-surface2,#112538)"}}>
              {/* Esquina: sticky en AMBOS ejes (left+top), zIndex mayor que headers y columna Estado */}
              <th style={{position:"sticky",left:0,top:0,zIndex:4,background:"var(--c-surface2,#112538)",padding:".55rem .6rem",textAlign:"left",color:"var(--c-text2,#7aa3c0)",fontWeight:700,borderBottom:"1px solid var(--c-border,#1a3550)",whiteSpace:"nowrap",width:ancho,minWidth:ancho,maxWidth:ancho,boxSizing:"border-box"}}>
                {es?"Estado":"State"}
                <div onPointerDown={empezarArrastre} onDoubleClick={()=>setAnchoEstado(ANCHO_ESTADO)}
                  title={es?"Arrastra para cambiar el ancho (doble clic: ancho normal)":"Drag to resize (double-click: default width)"}
                  style={{position:"absolute",top:0,right:0,width:8,height:"100%",cursor:"col-resize",borderRight:"2px solid var(--c-border2,#1f3f60)",touchAction:"none"}}/>
              </th>
              {data.carriers.map(c => {
                const isCustom = customCarriersForTab.includes(c);
                return (
                  // Fixed 78px columns + wrapping (whiteSpace:normal + wordBreak)
                  // so long names like "Northwestern Mutual" break onto 2 lines
                  // instead of overflowing and colliding (Lissette 2026-07-14).
                  <th key={c} style={{padding: showManage?".95rem .35rem .4rem":".45rem .4rem",textAlign:"center",color:isCustom?"var(--c-cyan,#00d4ff)":"var(--c-text2,#7aa3c0)",fontWeight:600,borderBottom:"1px solid var(--c-border,#1a3550)",whiteSpace:"normal",wordBreak:"break-word",fontSize:".62rem",lineHeight:1.15,width:78,minWidth:78,maxWidth:78,verticalAlign:"bottom",position:"sticky",top:0,zIndex:3,background:"var(--c-surface2,#112538)"}}>
                    {/* When "Manage" is on, ANY carrier (built-in or custom) can be
                        removed; built-ins are hidden (restorable), custom deleted. */}
                    {showManage && (
                      <button onClick={()=>removeCarrier(c)} title={es?"Quitar":"Remove"} style={{position:"absolute",top:2,right:2,background:"none",border:"none",color:"#ef4444",cursor:"pointer",padding:0,lineHeight:0}}><X size={10}/></button>
                    )}
                    {c}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredStates.map((st, si) => {
              return (
                <tr key={st.s} style={{background:si%2===0?"transparent":"rgba(255,255,255,.015)"}}>
                  <td style={{position:"sticky",left:0,zIndex:1,background:si%2===0?"var(--c-surface,#0d1c2e)":"var(--c-surface2,#112538)",padding:".45rem .6rem",fontWeight:700,color:"var(--c-white,#eef6ff)",borderBottom:"1px solid var(--ci-bg-04)",whiteSpace:"nowrap",fontSize:".72rem",width:ancho,minWidth:ancho,maxWidth:ancho,boxSizing:"border-box",overflow:"hidden",textOverflow:"ellipsis"}} title={st.s}>
                    {st.s}
                  </td>
                  {data.carriers.map(carrier => {
                    const on = isOn(st.s, carrier);
                    const refName = st.c ? st.c[carrier] : undefined;
                    return (
                      <td key={carrier} style={{padding:".3rem .2rem",textAlign:"center",borderBottom:"1px solid var(--ci-bg-04)",cursor:"pointer"}}
                        onClick={() => toggle(st.s, carrier)}
                        title={refName ? `${carrier}: ${refName}` : carrier}>
                        <div style={{
                          width:28,height:28,borderRadius:6,margin:"0 auto",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          background:on?"var(--c-green,#00ff88)":"var(--ci-bg-06)",
                          border:`1.5px solid ${on?"rgba(0,255,136,.6)":"var(--c-border,#1a3550)"}`,
                          color:on?"#071828":"var(--c-text3,#45688a)",
                          fontSize:".65rem",fontWeight:800,transition:"all .12s",
                          boxShadow:on?"0 0 8px rgba(0,255,136,.25)":"none"
                        }}>
                          {on ? "✓" : ""}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{marginTop:".75rem",display:"flex",gap:"1rem",alignItems:"center",flexWrap:"wrap",fontSize:".7rem",color:"var(--c-text3,#45688a)"}}>
        <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
          <div style={{width:14,height:14,borderRadius:4,background:"var(--c-green,#00ff88)",border:"1.5px solid rgba(0,255,136,.6)"}}/>
          {es?"= Activa":"= Active"}
        </span>
        <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
          <div style={{width:14,height:14,borderRadius:4,background:"var(--ci-bg-06)",border:"1.5px solid var(--c-border,#1a3550)"}}/>
          {es?"= No activa":"= Not active"}
        </span>
        <span style={{marginLeft:"auto",fontStyle:"italic"}}>{readOnly ? (es?"Vista de solo lectura":"Read-only view") : (es?"Haz clic en una celda para activar/desactivar":"Click a cell to toggle on/off")}</span>
      </div>
    </div>
  );
}
